import React from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { ItemHubEvents, ItemHubMethods } from "../../src/taskpane/interfaces/signalR/IItemHub";
import { ItemStatusBroadcast } from "../../src/taskpane/interfaces/signalR/ItemStatusBroadcast";
import { logger } from "../../utilities/logger";
import { IItemBroadcastHandlers, routeItemBroadcast } from "./helpers/itemBroadcastRouting";

/**
 * ItemHub (SignalR) connection for the cross-library "All documents" grid — the
 * aggregate twin of the single-library grid's connection in useContentManagerBody.
 *
 * One connection, MANY groups: the hub groups clients per list id, so the aggregate view
 * enlists every displayed library on the same socket and routes each broadcast back to
 * its source library (see helpers/itemBroadcastRouting). The connection is rebuilt when
 * the library SET changes — the hub exposes no way to leave a group, so shedding one
 * requires a fresh connection.
 */

// Escalating, capped: the same 5 s first retry the library grid uses, but this view is
// long-lived and also retries after `withAutomaticReconnect` gives up — a flat 5 s would
// mean ~720 failed negotiations an hour against a hub that is simply down. The counter
// resets on every successful start, so a drop after hours of health still retries fast.
const RECONNECT_DELAYS_MS = [5000, 10000, 30000, 60000];

export interface IUseAggregatedItemHubOptions {
    /** Every library the view shows; each becomes a hub group membership. */
    listIds: string[];
    /** Connect at all (the caller's realtime gate). */
    enabled: boolean;
    currentAccountId?: string;
    externalContentLabel?: string;
    handlers: IItemBroadcastHandlers;
}

export const useAggregatedItemHub = ({
    listIds,
    enabled,
    currentAccountId,
    externalContentLabel,
    handlers,
}: IUseAggregatedItemHubOptions): void => {
    // Handlers/context are rebuilt on every host render. Reading them through refs keeps
    // the effect keyed on the library set ALONE — keying it on the callbacks would tear
    // the socket down and re-enlist every group on each render.
    const handlersRef = React.useRef<IItemBroadcastHandlers>(handlers);
    handlersRef.current = handlers;
    const accountIdRef = React.useRef<string>(currentAccountId);
    accountIdRef.current = currentAccountId;
    const externalContentLabelRef = React.useRef<string>(externalContentLabel);
    externalContentLabelRef.current = externalContentLabel;

    // Sorted join: the libraries array is rebuilt as item counts resolve, but the
    // connection only cares whether the SET of list ids changed.
    const listIdsKey = React.useMemo(
        () => Array.from(new Set((listIds ?? []).filter(Boolean))).sort().join("|"),
        [listIds]
    );
    const currentListIds: string[] = React.useMemo(() => listIdsKey.split("|").filter(Boolean), [listIdsKey]);
    // Read by enlisting and routing at the time they run, so a library that joined later counts.
    const listIdsRef = React.useRef<string[]>(currentListIds);
    listIdsRef.current = currentListIds;

    // The connection is rebuilt only when a library LEAVES the set: the hub has no way to leave
    // a group. A library that JOINS (client-group libraries join after the site's own) is
    // enlisted on the open connection by the effect below.
    const connectionEpochRef = React.useRef<{ epoch: number; listIds: string[] }>({ epoch: 0, listIds: currentListIds });
    if (connectionEpochRef.current.listIds !== currentListIds) {
        const isShrink = connectionEpochRef.current.listIds.some(listId => currentListIds.indexOf(listId) === -1);
        connectionEpochRef.current = { epoch: connectionEpochRef.current.epoch + (isShrink ? 1 : 0), listIds: currentListIds };
    }
    const connectionEpoch: number = connectionEpochRef.current.epoch;
    const hasListIds: boolean = currentListIds.length > 0;

    // The open connection and the lists enlisted on it.
    const liveConnectionRef = React.useRef<{ connection: HubConnection; enlisted: Set<string> } | null>(null);

    React.useEffect(() => {
        if (!enabled || !hasListIds) {
            return undefined;
        }

        let isDisposed = false;
        let retryHandle: ReturnType<typeof setTimeout> | undefined;
        let retryAttempt = 0;

        const connection: HubConnection = new HubConnectionBuilder()
            .withUrl(`${process.env.HUB_URL}/itemhub`, { withCredentials: false })
            .withAutomaticReconnect()
            .withStatefulReconnect()
            .configureLogging(LogLevel.None)
            .build();
        const live = { connection, enlisted: new Set<string>() };
        liveConnectionRef.current = live;

        const enlistAll = async (connectionId?: string): Promise<void> => {
            const id = connectionId ?? connection.connectionId;
            // Every list in the set NOW (including any that joined while connecting). Group
            // membership does not survive a reconnect, so this is the whole set each time.
            const enlistedListIds = listIdsRef.current.slice();
            live.enlisted = new Set(enlistedListIds);
            // Concurrent, not sequential: N libraries would otherwise cost N round-trips
            // before the first update can arrive. One failed group must not skip the rest.
            await Promise.all(enlistedListIds.map(async listId => {
                try {
                    await connection.invoke(ItemHubMethods.EnlistClientConnection, id, listId);
                } catch (error) {
                    logger(error, "useAggregatedItemHub.ts (enlistClientConnection)");
                }
            }));
        };

        // Single owner of the retry timer: onclose and a failed start can both want one,
        // and overwriting a pending handle would leak a second concurrent retry chain.
        const scheduleStart = (): void => {
            if (isDisposed) {
                return;
            }
            clearTimeout(retryHandle);
            const delay = RECONNECT_DELAYS_MS[Math.min(retryAttempt++, RECONNECT_DELAYS_MS.length - 1)];
            retryHandle = setTimeout(start, delay);
        };

        const start = async (): Promise<void> => {
            // start() throws unless the connection is fully Disconnected, so the state
            // check (not a boolean flag) is what makes concurrent callers safe.
            if (isDisposed || connection.state !== HubConnectionState.Disconnected) {
                return;
            }
            try {
                await connection.start();
                if (isDisposed) {
                    // Unmounted while the handshake was in flight — the cleanup's stop()
                    // ran against a not-yet-started connection, so stop it here.
                    connection.stop().catch(() => undefined);
                    return;
                }
                retryAttempt = 0;
                await enlistAll();
            } catch {
                // Hub unreachable (offline, cold start): keep retrying, but never past
                // unmount — an unguarded retry chain outlives the view forever.
                scheduleStart();
            }
        };

        connection.onreconnected((connectionId?: string) => {
            // Group membership does not survive a reconnect — re-enlist every library.
            if (isDisposed) {
                return;
            }
            enlistAll(connectionId).catch(error => logger(error, "useAggregatedItemHub.ts (onreconnected)"));
        });

        connection.onclose(() => {
            // withAutomaticReconnect gives up after its four attempts (~40 s). This view
            // is meant to stay open for hours, so a laptop sleeping through that window
            // would otherwise leave the grid silently stale until a manual refresh —
            // resume the retry loop instead. isDisposed keeps our own cleanup stop()
            // (which also fires onclose) from resurrecting the connection.
            scheduleStart();
        });

        connection.on(ItemHubEvents.ReceiveStatusUpdate, (broadcast: ItemStatusBroadcast) => {
            if (isDisposed) {
                return;
            }
            try {
                routeItemBroadcast(
                    broadcast,
                    {
                        enlistedListIds: listIdsRef.current,
                        currentAccountId: accountIdRef.current,
                        externalContentLabel: externalContentLabelRef.current,
                    },
                    handlersRef.current
                );
            } catch (error) {
                logger(error, "useAggregatedItemHub.ts (receiveStatusUpdate)");
            }
        });

        start();

        return () => {
            isDisposed = true;
            clearTimeout(retryHandle);
            if (liveConnectionRef.current === live) {
                liveConnectionRef.current = null;
            }
            connection.stop().catch(() => undefined);
        };
    }, [enabled, hasListIds, connectionEpoch]);

    // A library that joined the set is enlisted on the open connection. One still starting
    // (or reconnecting) enlists the whole current set itself once it is up.
    React.useEffect(() => {
        const live = liveConnectionRef.current;
        if (!live || live.connection.state !== HubConnectionState.Connected) {
            return;
        }
        currentListIds
            .filter(listId => !live.enlisted.has(listId))
            .forEach(listId => {
                live.enlisted.add(listId);
                live.connection.invoke(ItemHubMethods.EnlistClientConnection, live.connection.connectionId, listId)
                    .catch(error => logger(error, "useAggregatedItemHub.ts (enlist joined library)"));
            });
    }, [currentListIds]);
};
